import {type MatchParams} from '@wandb/weave/common/types/base';

import {makeTrackEvent} from './makeTrackEvent';

export type AgentChatLocation =
  | 'entity'
  | 'project'
  | 'run'
  | 'report'
  | 'global';

export type AgentChatAction =
  | 'navbar_button_viewed'
  | 'opened'
  | 'closed'
  | 'docked'
  | 'undocked'
  | 'chat_created'
  | 'chat_switched'
  | 'chat_renamed'
  | 'chat_deleted'
  | 'mention_added'
  | 'mention_removed'
  | 'suggestion_clicked'
  | 'message_sent'
  | 'message_queued'
  | 'queued_message_sent'
  | 'queued_message_steered'
  | 'queued_message_recalled'
  | 'queued_message_removed'
  | 'turn_stopped'
  | 'step_expanded'
  | 'step_collapsed'
  | 'copied'
  | 'feedback_submitted';

export type AgentChatMentionVia =
  | 'composer_dropdown'
  | 'run_row_menu'
  | 'panel_overflow_menu'
  | 'context_chip';
export type AgentChatMentionKind = 'run' | 'project' | 'panel';
export type AgentChatStepKind = 'tool_call' | 'reasoning' | 'step_group';
export type AgentChatCopyTarget = 'message' | 'tool_call' | 'error';
export type AgentChatMessageVia =
  | 'composer'
  | 'suggestion'
  | 'programmatic'
  | 'tool_ui';

export type AgentChatInteractionPayload = {
  action: AgentChatAction;
  location: AgentChatLocation;
  entity_name?: string;
  project_name?: string;
  chat_id?: string;
  mention_via?: AgentChatMentionVia;
  mention_kind?: AgentChatMentionKind;
  suggestion_label?: string;
  step_kind?: AgentChatStepKind;
  tool_name?: string;
  copy_target?: AgentChatCopyTarget;
  feedback_is_positive?: boolean | null;
  feedback_has_reasoning?: boolean;
  message_via?: AgentChatMessageVia;
  queue_depth?: number;
  queue_position?: number;
};

/**
 * AgentChatTrackContext is the route and chat scope every interaction carries,
 * shared by the trackers that assemble payloads around it.
 */
export type AgentChatTrackContext = Pick<
  AgentChatInteractionPayload,
  'location' | 'entity_name' | 'project_name' | 'chat_id'
>;

export const trackAgentChatInteraction = makeTrackEvent<
  AgentChatInteractionPayload,
  {
    action: {
      description: 'which agent-chat interaction the user performed';
    };
    location: {
      description: 'route context the user was in when the action fired';
    };
    entity_name: {
      description: 'entity (team or user) in the current route, when present';
      exampleValues: ['my-team', 'my-user'];
    };
    project_name: {
      description: 'project in the current route, when present';
      exampleValues: ['my-project'];
    };
    chat_id: {
      description: 'active agent chat id when known';
    };
    mention_via: {
      description: 'how the user added the mention';
    };
    mention_kind: {
      description: 'type of entity mentioned';
    };
    suggestion_label: {
      description: 'title of the suggestion card the user pressed';
      exampleValues: ['Summarize project', 'Recommend next experiment'];
    };
    step_kind: {
      description: 'which disclosure type was toggled';
    };
    tool_name: {
      description: 'tool name when step_kind is tool_call';
      exampleValues: ['shell', 'apply_patch'];
    };
    copy_target: {
      description: 'what type of content was copied to the clipboard';
    };
    feedback_is_positive: {
      description: 'thumbs up (true), thumbs down (false), or retraction (null)';
      exampleValues: [true, false, null];
    };
    feedback_has_reasoning: {
      description: 'whether the user supplied a reasoning string';
    };
    message_via: {
      description: 'how the message was submitted';
    };
    queue_depth: {
      description: 'queued messages at the time of the action, counting the one acted on';
      exampleValues: [1, 4];
    };
    queue_position: {
      description: '0-based slot of the queued message acted on, head first';
      exampleValues: [0, 2];
    };
    _description: 'User interactions with the W&B Agent UI';
    _location: 'W&B Agent chat button, panel, composer, queued messages, history, message actions';
    _motivation: 'Measure adoption and per-surface engagement to guide UX investment';
  }
>('WB Agent Interaction');

/**
 * getTrackContext snapshots the route and chat scope every interaction
 * payload carries, so trackers on either side of the components/agentChat
 * boundary assemble it identically.
 */
export function getTrackContext(
  params: MatchParams,
  chatId: string | undefined
): AgentChatTrackContext {
  return {
    location: getLocationFromParams(params),
    entity_name: params.entityName,
    project_name: params.projectName,
    chat_id: chatId,
  };
}

/**
 * getLocationFromParams derives an event's `location` from the current route,
 * picking the most specific scope — run > report > project > entity > global.
 */
function getLocationFromParams(params: MatchParams): AgentChatLocation {
  if (params.runName) {
    return 'run';
  }
  if (params.reportNameAndID) {
    return 'report';
  }
  if (params.projectName) {
    return 'project';
  }
  if (params.entityName) {
    return 'entity';
  }
  return 'global';
}
